import { IScore } from "@eagerdog/interfaces";

/** Strip the age-range suffix: "Open (6 months & over)" → "Open". */
export function shortLevel(level: string): string {
  return level.replace(/\s*\([^)]*\)\s*$/, "");
}

/**
 * Picker label for a conformation entry. Armband first so secretaries can
 * work from paper: `#142 Fluffy`, `#142 Fluffy — Poodle`, `#12 Jordan Smith` (junior),
 * `#88 Rex & Roxy` (brace).
 */
export function dogLabel(
  score: IScore,
  options?: { breed?: boolean; junior?: boolean }
): string {
  // Junior Showmanship runs on the junior armband; a junior's dog may also carry a
  // breed number, which is what the judge's book / ribbons do not use for this class.
  const arm = options?.junior
    ? score.arm_number_junior || score.arm_number || ""
    : score.arm_number || score.arm_number_junior || "";
  const armPart = arm ? `#${arm}` : "";
  if (options?.junior) {
    const name = score.agent_name || score.owner_name || score.call_name || "Junior";
    return [armPart, name].filter(Boolean).join(" ");
  }
  const leadName = score.call_name || score.registered_name || "Dog";
  const companion = score.dog_class?.extra_dogs?.[0];
  const companionName = companion?.call_name || companion?.registered_name;
  const name = companionName ? `${leadName} & ${companionName}` : leadName;
  if (options?.breed && score.breed) {
    return `${[armPart, name].filter(Boolean).join(" ")} — ${score.breed}`;
  }
  return [armPart, name].filter(Boolean).join(" ");
}

/** Lower-cased text a picker search should match against for one entry. */
export function dogSearchText(score: IScore): string {
  const companion = score.dog_class?.extra_dogs?.[0];
  return [
    score.arm_number,
    score.arm_number_junior,
    score.call_name,
    score.registered_name,
    score.agent_name,
    score.owner_name,
    score.breed,
    companion?.call_name,
    companion?.registered_name,
  ]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();
}

/** Saved winners stay at the top. Everyone else stays selectable, by numeric armband. */
export function preferScores(
  scores: IScore[],
  preferredIds: Array<string | undefined>,
  options?: { junior?: boolean }
): IScore[] {
  const rank = new Map<string, number>();
  preferredIds.filter((id): id is string => !!id).forEach((id, index) => {
    if (!rank.has(id)) rank.set(id, index);
  });
  const armOf = (score: IScore) =>
    (options?.junior ? score.arm_number_junior || score.arm_number : score.arm_number) || "";
  return [...scores].sort((a, b) => {
    const ar = rank.has(a._id) ? rank.get(a._id)! : 1000;
    const br = rank.has(b._id) ? rank.get(b._id)! : 1000;
    if (ar !== br) return ar - br;
    return armOf(a).localeCompare(armOf(b), undefined, { numeric: true });
  });
}

/** Always four slots: [1st, 2nd, 3rd, 4th]. */
export function padPlacements(ids?: string[]): string[] {
  return [...(ids || []), "", "", "", ""].slice(0, 4);
}

export const PLACE_LABELS = ["1st", "2nd", "3rd", "4th"];
